// assets/delivery-estimate.js
(function (root) {
  if (root.themeDeliveryEstimate) {
    return;
  }

  const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
  const CUTOFF = /^([01]?\d|2[0-3]):([0-5]\d)$/;
  const DEFAULT_CUTOFF_MINUTES = 14 * 60;
  const madridClock = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Madrid',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  });

  function parseHolidays(text) {
    const holidays = new Set();
    String(text || '')
      .split(/\s+/)
      .forEach(function (line) {
        if (ISO_DAY.test(line)) {
          holidays.add(line);
        }
      });
    return holidays;
  }

  // Calendar days are handled as 'YYYY-MM-DD' strings on UTC midnights, so the
  // browser's own timezone never shifts a date.
  function toDate(ymd) {
    const parts = ymd.split('-').map(Number);
    return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  }

  function isWorkingDay(ymd, holidays) {
    const weekday = toDate(ymd).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !holidays.has(ymd);
  }

  function addWorkingDays(ymd, days, holidays) {
    const date = toDate(ymd);
    let left = days;
    while (left > 0) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (isWorkingDay(date.toISOString().slice(0, 10), holidays)) {
        left -= 1;
      }
    }
    return date.toISOString().slice(0, 10);
  }

  function madridNow(now) {
    const parts = {};
    madridClock.formatToParts(now).forEach(function (part) {
      parts[part.type] = part.value;
    });
    return {
      day: parts.year + '-' + parts.month + '-' + parts.day,
      minutes: (Number(parts.hour) % 24) * 60 + Number(parts.minute)
    };
  }

  function cutoffMinutes(cutoff) {
    const match = CUTOFF.exec(String(cutoff || '').trim());
    return match ? Number(match[1]) * 60 + Number(match[2]) : DEFAULT_CUTOFF_MINUTES;
  }

  // Orders after the cutoff, or on a non-working day, dispatch the next working day.
  function dispatchDay(now, cutoff, holidays) {
    const local = madridNow(now);
    if (isWorkingDay(local.day, holidays) && local.minutes < cutoffMinutes(cutoff)) {
      return { day: local.day, beforeCutoff: true };
    }
    return { day: addWorkingDays(local.day, 1, holidays), beforeCutoff: false };
  }

  function days(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  function estimate(now, config, lead) {
    const holidays = config.holidaySet || parseHolidays(config.holidays);
    const dispatch = dispatchDay(now, config.cutoff, holidays);
    const leadMin = lead ? days(config.leadMin, 1) : 0;
    const leadMax = lead ? days(config.leadMax, 7) : 0;
    const first = leadMin + days(config.transitMin, 1);
    const last = Math.max(first, leadMax + days(config.transitMax, 2));
    return {
      date1: addWorkingDays(dispatch.day, first, holidays),
      date2: addWorkingDays(dispatch.day, last, holidays),
      beforeCutoff: dispatch.beforeCutoff
    };
  }

  function part(date, locale, options) {
    options.timeZone = 'UTC';
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(locale, options);
    } catch (error) {
      formatter = new Intl.DateTimeFormat('es', options);
    }
    return formatter.format(date).replace(/\.$/, '');
  }

  // "jue 15 oct" / "Thu 15 Oct" / "quinta 15 out". Weekday and month are formatted
  // separately because some locales (pt-PT) switch to a numeric month when combined.
  function formatDay(ymd, locale) {
    const date = toDate(ymd);
    return [
      part(date, locale, { weekday: 'short' }),
      date.getUTCDate(),
      part(date, locale, { month: 'short' })
    ].join(' ');
  }

  let cachedConfig = null;

  function readConfig() {
    if (cachedConfig) {
      return cachedConfig;
    }
    const node = document.getElementById('DeliveryEstimateConfig');
    if (!node) {
      return null;
    }
    try {
      cachedConfig = JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
    cachedConfig.holidaySet = parseHolidays(cachedConfig.holidays);
    return cachedConfig;
  }

  const PLACEHOLDER = /__min__\s*[–-]\s*__max__|__(cutoff|date1|date2|min|max)__/g;

  // Splits a template into text segments; the day range, cutoff and dates are
  // marked strong so a skimming reader catches them. Values never become HTML.
  function emphasize(template, values) {
    const source = String(template || '');
    const segments = [];
    let last = 0;
    source.replace(PLACEHOLDER, function (match, key, offset) {
      if (offset > last) {
        segments.push({ text: source.slice(last, offset), strong: false });
      }
      segments.push({ text: key ? values[key] : values.min + '–' + values.max, strong: true });
      last = offset + match.length;
      return match;
    });
    if (last < source.length || segments.length === 0) {
      segments.push({ text: source.slice(last), strong: false });
    }
    return segments;
  }

  function render(element, now) {
    const config = readConfig();
    const text = element.querySelector('[data-delivery-estimate-text]');
    if (!config || !text) {
      return;
    }
    const lead = element.dataset.lead === '1' ? 1 : 0;
    const data = element.dataset;
    element.classList.toggle('delivery-estimate--lead', lead === 1);

    let template;
    if (data.context === 'cart') {
      template = lead ? data.textSummaryLead + ' ' + data.textOrder : data.textOrder;
    } else if (lead) {
      template = data.textLead;
    } else {
      template = null;
    }
    const result = estimate(now || new Date(), config, lead);
    if (template === null) {
      template = result.beforeCutoff ? data.textBeforeCutoff : data.textAfterCutoff;
    }
    const minutes = cutoffMinutes(config.cutoff);
    const segments = emphasize(template, {
      min: String(days(config.leadMin, 1)),
      max: String(days(config.leadMax, 7)),
      cutoff: String(Math.floor(minutes / 60)).padStart(2, '0') + ':' + String(minutes % 60).padStart(2, '0'),
      date1: formatDay(result.date1, config.locale),
      date2: formatDay(result.date2, config.locale)
    });
    text.replaceChildren.apply(text, segments.map(function (segment) {
      if (!segment.strong) {
        return segment.text;
      }
      const strong = document.createElement('strong');
      strong.textContent = segment.text;
      return strong;
    }));
  }

  function refresh(scope) {
    (scope || document).querySelectorAll('[data-delivery-estimate]').forEach(function (element) {
      render(element);
    });
  }

  root.themeDeliveryEstimate = {
    parseHolidays: parseHolidays,
    isWorkingDay: isWorkingDay,
    addWorkingDays: addWorkingDays,
    dispatchDay: dispatchDay,
    estimate: estimate,
    formatDay: formatDay,
    emphasize: emphasize,
    render: render,
    refresh: refresh
  };

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        refresh();
      });
    } else {
      refresh();
    }
  }
})(window);
